import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { SearchNavBar, DataTable } from '../../components/ui';
import { AppStrings, logApiErrorToSentry } from '../../utils';
import { remittanceActions } from '../../state/remittanceSlice';
import { RemitterBanner, SendMoneyIcon } from '../../../../assets/Icons';
import KycStatus from '../../components/ui/KycStatus';
import RemitterDetails from "./RemitterDetails";
import { USERDETAILS_DATA } from '../../../../utils';
import { remittanceApi } from '../../api/remittanceApi';
import Typograpy from '../../../../components/ui/Typography';
import { DebounceInput } from 'react-debounce-input';
import SelectDropdown from '../../components/ui/SelectDropdown';
import Checkbox from '../../../../components/ui/Checkbox';
import Button from '../../../../components/ui/Button';

const RemittanceRemitterView: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const remitterState = useAppSelector((state) => state.remittance);
  const authState = useAppSelector((state) => state.auth);
  const { calculator, remitter } = useAppSelector((state) => state.remittance);

  const [searchedInput, setSearchedInput] = React.useState<string>("");

  const IndustryTypeList = USERDETAILS_DATA.industryType;
  const EmploymentStatusList = USERDETAILS_DATA.employmentStatus;
  const FundSourceList = USERDETAILS_DATA.fundSource;
  const NationalityList = USERDETAILS_DATA.nationality;
  const StateList = USERDETAILS_DATA.states;
  const GenderList = USERDETAILS_DATA.gender;

  const fullName = `${remitter?.updateRemitterInput?.firstName} ${remitter?.updateRemitterInput?.middleName} ${remitter?.updateRemitterInput?.lastName}`;
  const verifyButtonDisable = !fullName ||
    !remitter?.updateRemitterInput?.dob ||
    !remitter?.updateRemitterInput?.gender ||
    !remitter?.updateRemitterInput?.addressLine1 ||
    !remitter?.updateRemitterInput?.addressLine2 ||
    !remitter?.updateRemitterInput?.city ||
    !remitter?.updateRemitterInput?.state ||
    !remitter?.updateRemitterInput?.nationality ||
    !remitter?.updateRemitterInput?.pinCode ||
    !remitter?.updateRemitterInput?.employmentStatus ||
    !remitter?.updateRemitterInput?.industryType;

  {/* Initial API call and condition when user came to this page */}
  useEffect(() => {
    dispatch(remittanceActions.setGetRemitter({ loading: true, error: null, data: null }));
    remittanceApi.getRemitterDetails(remitter?.selectedRemitter?.userKey)
      .then((response) => {
        dispatch(remittanceActions.setSelectedRemitter({...response?.data, userKey: remitter?.selectedRemitter?.userKey }));
        dispatch(remittanceActions.setGetRemitter({ loading: false, error: null, data: response?.data }));
        dispatch(remittanceActions.setUpdateRemitterInput(response?.data));
      })
      .catch((err) => {
        const errorMsg = err?.response?.data?.error?.type || err?.response?.error?.type || err?.response?.error || AppStrings.GENERIC_ERROR;
        toast.error(errorMsg);
        dispatch(remittanceActions.setGetRemitter({ loading: false, error: errorMsg, data: null }));
        logApiErrorToSentry(err, "remittanceApi.getRemitterDetails");
      })
  }, []);

  const handleSumbmitRemitter = () => {
    const body = {
      firstName: fullName,
      middleName: "",
      lastName: "",
      gender: remitter?.updateRemitterInput?.gender,
      phoneNumber: "",
      nationality: remitter?.updateRemitterInput?.nationality,
      addressLine1: remitter?.updateRemitterInput?.addressLine1,
      addressLine2: remitter?.updateRemitterInput?.addressLine2,
      state: remitter?.updateRemitterInput?.state,
      city: remitter?.updateRemitterInput?.city,
      pinCode: remitter?.updateRemitterInput?.pinCode,
      dob: remitter?.updateRemitterInput?.dob,
      isPhoneVerified: true,
      hop_coins: 0,
      isPolitical: remitter?.updateRemitterInput?.isPolitical,
      employmentStatus: remitter?.updateRemitterInput?.employmentStatus,
      industryType: remitter?.updateRemitterInput?.industryType,
      userKey: authState.currentUser?.vendor_key,
      isDocVerified: true,
      createdOn: "",
      modifiedOn: "",
      isMasterVendor: false,
    };
    dispatch(remittanceActions.setUpdateRemitter({ loading: true, error: null, data: null }));
    remittanceApi.updateRemitterDetails(remitter?.selectedRemitter?.userKey, body)
      .then((response) => {
        dispatch(remittanceActions.setUpdateRemitter({ loading: false, error: null, data: response?.data }));
        dispatch(remittanceActions.setActiveStepNumber(3));
        dispatch(remittanceActions.resetRecipientState());
        navigate("/send/recipient");
      })
      .catch((err) => {
        const errorMsg = err?.response?.data?.error?.type || err?.response?.error?.type || err?.response?.error || AppStrings.GENERIC_ERROR;
        toast.error(errorMsg);
        dispatch(remittanceActions.setUpdateRemitter({ loading: false, error: errorMsg, data: null }));
        logApiErrorToSentry(err, "remittanceApi.handleSumbmitRemitter");
      })

  }

  useEffect(() => {
    if(remitterState?.progresStep?.activeStepNumber < 2) {
      navigate("/send/calculator", { replace: true });
    }
  }, [remitterState?.progresStep?.activeStepNumber]);

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return(
    <>
      <div className="max-w-[1440px] w-full mx-auto px-[160px]">
        <ToastContainer autoClose={3000} position="top-right" />

        <div id="header" className="w-[1280px] h-10 flex justify-between items-center">
          <div id="left" className="flex items-center space-x-3">
            <div>
              <SendMoneyIcon />
            </div>
            <span className="text-[32px] font-bold">Remitter Details</span>
            <span className="mt-3 ml-10 font-semibold">Enter basic details of the remitter</span>
          </div>
        </div>

        {remitter?.getRemitter?.loading && (
          <div className="p-4 flex justify-center items-center">
            <div className="animate-spin h-6 w-6 border-2 border-t-blue-500 rounded-full" />
          </div>
        )}

        {remitter?.getRemitter?.error && (
          <div className="p-4 flex justify-center items-center">
            <Typograpy tagType="div" text={remitter?.getRemitter?.error} className="text-sm font-normal text-red-100" />
          </div>
        )}
        
        {!remitter?.getRemitter?.loading && !remitter?.getRemitter?.error && (
        <div className="flex flex-col w-[1280px]">

          <div className="mt-5">

            <span className="pl-3 py-1 w-fit border-l-4 border-blue-500 text-sm font-semibold text-gray-800">
              Basic Details
              <span className="text-red-500 ml-1">*</span>
            </span>
            
            <div className="flex gap-5 mt-5">

              <div>
                <div className="w-[350px]">
                  <span className="block text-black-100 text-left text-[15px]">
                    Full Name
                    <span className="text-red-100 text-sm">*</span>
                  </span>
                </div>
                <DebounceInput
                  className={"w-[350px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none disabled:bg-gray-100"}
                  name="bankName"
                  type="text"
                  value={`${remitter?.updateRemitterInput?.firstName} ${remitter?.updateRemitterInput?.middleName} ${remitter?.updateRemitterInput?.lastName}`}
                  debounceTimeout={0}
                  onChange={(e: any) => {
                    
                  }}
                  autoComplete='off'
                  fullWidth
                  inputClasses="text-[20px] bg-blue-100"
                  disabled={true}
                  placeholder={""}
                />
              </div>

              <div>
                <div className="w-[350px]">
                  <span className="block text-black-100 text-left text-[15px]">
                    Date of birth
                    <span className="text-red-100 text-sm">*</span>
                  </span>
                </div>
                <DebounceInput
                  className={"w-[350px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none disabled:bg-gray-100"}
                  name="bankName"
                  type="text"
                  value={remitter?.updateRemitterInput?.dob}
                  debounceTimeout={0}
                  onChange={(e: any) => {
                    
                  }}
                  autoComplete='off'
                  fullWidth
                  inputClasses="text-[20px] bg-blue-100"
                  disabled={true}
                  placeholder={""}
                />
              </div>

              <div>
                <div className="w-[350px]">
                  <span className="block text-black-100 text-left text-[15px]">
                    Gender
                    <span className="text-red-100 text-sm">*</span>
                  </span>
                </div>
                <SelectDropdown
                  data={GenderList}
                  value={remitter?.updateRemitterInput?.gender}
                  onChange={(response) => {
                    dispatch(remittanceActions.setUpdateRemitterInput({ gender: response }));
                  }}
                  getLabel={(item) => item}
                  getValue={(item) => item}
                  width={"350px"}
                  placeHolderFontSize={"16px"}
                  placeHolderColor={"#a6a6a6"}
                  textSize={"16px"}
                  fontWeight={100}
                  backgroundColor="#fff"
                  isSearchable={true}
                  isLoading={false}
                />
              </div>

            </div>

            <div className="mt-5">
              <span className="pl-3 py-1 w-fit border-l-4 border-blue-500 text-sm font-semibold text-gray-800">
                Permanent Address
                <span className="text-red-500 ml-1">*</span>
              </span>
            </div>

            <div className="flex gap-5 mt-5">

              <div>
                <div className="w-[350px]">
                  <span className="block text-black-100 text-left text-[15px]">
                    Address Line 1
                    <span className="text-red-100 text-sm">*</span>
                  </span>
                </div>
                <DebounceInput
                  className={"w-[350px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none"}
                  name="addressLine1"
                  type="text"
                  value={remitter?.updateRemitterInput?.addressLine1}
                  debounceTimeout={0}
                  onChange={(e: any) => {
                    const value = e?.target?.value?.trim() || "";
                    dispatch(remittanceActions.setUpdateRemitterInput({ addressLine1: value }));
                  }}
                  autoComplete='off'
                  fullWidth
                  inputClasses="text-[20px] bg-blue-100"
                  disabled={false}
                  placeholder={""}
                />
              </div>

              <div>
                <div className="w-[350px]">
                  <span className="block text-black-100 text-left text-[15px]">
                    Address Line 2
                    <span className="text-red-100 text-sm">*</span>
                  </span>
                </div>
                <DebounceInput
                  className={"w-[350px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none"}
                  name="addressLine2"
                  type="text"
                  value={remitter?.updateRemitterInput?.addressLine2}
                  debounceTimeout={0}
                  onChange={(e: any) => {
                    const value = e?.target?.value?.trim() || "";
                    dispatch(remittanceActions.setUpdateRemitterInput({ addressLine2: value }));
                  }}
                  autoComplete='off'
                  fullWidth
                  inputClasses="text-[20px] bg-blue-100"
                  disabled={false}
                  placeholder={""}
                />
              </div>

              <div>
                <div className="w-[350px]">
                  <span className="block text-black-100 text-left text-[15px]">
                    City
                    <span className="text-red-100 text-sm">*</span>
                  </span>
                </div>
                <DebounceInput
                  className={"w-[350px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none"}
                  name="city"
                  type="text"
                  value={remitter?.updateRemitterInput?.city}
                  debounceTimeout={0}
                  onChange={(e: any) => {
                    const value = e?.target?.value?.trim() || "";
                    dispatch(remittanceActions.setUpdateRemitterInput({ city: value }));
                  }}
                  autoComplete='off'
                  fullWidth
                  inputClasses="text-[20px] bg-blue-100"
                  disabled={false}
                  placeholder={""}
                />
              </div>

            </div>

            <div className="flex gap-5 mt-5">

              <div>
                <div className="w-[350px]">
                  <span className="block text-black-100 text-left text-[15px]">
                    State
                    <span className="text-red-100 text-sm">*</span>
                  </span>
                </div>
                <SelectDropdown
                  data={StateList}
                  value={remitter?.updateRemitterInput?.state?.toLowerCase().replace(/\b\w/g, (c: any) => c.toUpperCase())}
                  onChange={(response) => {
                    dispatch(remittanceActions.setUpdateRemitterInput({ state: response }));
                  }}
                  getLabel={(item) => item}
                  getValue={(item) => item}
                  width={"350px"}
                  placeHolderFontSize={"16px"}
                  placeHolderColor={"#a6a6a6"}
                  textSize={"16px"}
                  fontWeight={100}
                  backgroundColor="#fff"
                  isSearchable={true}
                  isLoading={false}
                />
              </div>

              <div>
                <div className="w-[350px]">
                  <span className="block text-black-100 text-left text-[15px]">
                    Nationality
                    <span className="text-red-100 text-sm">*</span>
                  </span>
                </div>
                <SelectDropdown
                  data={NationalityList}
                  value={remitter?.updateRemitterInput?.nationality}
                  onChange={(response) => {
                    dispatch(remittanceActions.setUpdateRemitterInput({ nationality: response }));
                  }}
                  getLabel={(item) => item}
                  getValue={(item) => item}
                  width={"350px"}
                  placeHolderFontSize={"16px"}
                  placeHolderColor={"#a6a6a6"}
                  textSize={"16px"}
                  fontWeight={100}
                  backgroundColor="#fff"
                  isSearchable={true}
                  isLoading={false}
                />
              </div>

              <div>
                <div className="w-[350px]">
                  <span className="block text-black-100 text-left text-[15px]">
                    Pincode
                    <span className="text-red-100 text-sm">*</span>
                  </span>
                </div>
                <DebounceInput
                  className={"w-[350px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none"}
                  name="pinCode"
                  type="text"
                  value={remitter?.updateRemitterInput?.pinCode}
                  debounceTimeout={0}
                  onChange={(e: any) => {
                    const value = e?.target?.value?.trim() || "";
                    dispatch(remittanceActions.setUpdateRemitterInput({ pinCode: value }));
                  }}
                  autoComplete='off'
                  fullWidth
                  inputClasses="text-[20px] bg-blue-100"
                  disabled={false}
                  placeholder={""}
                />
              </div>

            </div>

            <div className="mt-5">
              <span className="pl-3 py-1 w-fit border-l-4 border-blue-500 text-sm font-semibold text-gray-800">
                Other Details
                <span className="text-red-500 ml-1">*</span>
              </span>
            </div>


            <div className="flex gap-5 mt-5">

              <div>
                <div className="w-[350px]">
                  <span className="block text-black-100 text-left text-[15px]">
                    Employment Status
                    <span className="text-red-100 text-sm">*</span>
                  </span>
                </div>
                <SelectDropdown
                  data={EmploymentStatusList}
                  value={remitter?.updateRemitterInput?.employmentStatus ?? ""}
                  onChange={(response) => {
                    dispatch(remittanceActions.setUpdateRemitterInput({ employmentStatus: response }));
                  }}
                  getLabel={(item) => item}
                  getValue={(item) => item}
                  width={"350px"}
                  placeHolderFontSize={"16px"}
                  placeHolderColor={"#a6a6a6"}
                  textSize={"16px"}
                  fontWeight={100}
                  backgroundColor="#fff"
                  isSearchable={true}
                  isLoading={false}
                />
              </div>

              <div>
                <div className="w-[350px]">
                  <span className="block text-black-100 text-left text-[15px]">
                    Industry Type
                    <span className="text-red-100 text-sm">*</span>
                  </span>
                </div>
                <SelectDropdown
                  data={IndustryTypeList}
                  value={remitter?.updateRemitterInput?.industryType ?? ""}
                  onChange={(response) => {
                    dispatch(remittanceActions.setUpdateRemitterInput({ industryType: response }));
                  }}
                  getLabel={(item) => item}
                  getValue={(item) => item}
                  width={"350px"}
                  placeHolderFontSize={"16px"}
                  placeHolderColor={"#a6a6a6"}
                  textSize={"16px"}
                  fontWeight={100}
                  backgroundColor="#fff"
                  isSearchable={true}
                  isLoading={false}
                />
              </div>

            </div>

            <div className="flex items-center justify-start space-x-10">
              <div className="flex items-center space-x-4 mt-12">
                <Checkbox
                  label={"Are you a politically exposed person?"}
                  name={"checkbox"}
                  checked={remitter?.updateRemitterInput?.isPolitical ?? false}
                  inputClassname="flex justify-center w-full"
                  labelClassName="text-lg text-gray-500 font-ssp font-semibold"
                  // rootclassName="w-full"
                  handleChange={() => {
                    dispatch(remittanceActions.setUpdateRemitterInput({ isPolitical: remitter?.updateRemitterInput?.isPolitical == null ? true : !remitter?.updateRemitterInput?.isPolitical }));
                  }}
                  value={remitter?.updateRemitterInput?.isPolitical ?? false}
                />
              </div>
              <div id="last" className="flex items-center mt-10">
                <Button
                  bgColor={verifyButtonDisable ? "bg-gray-300" : "bg-[#006EFF]"}
                  fontStyle={{
                    fontFamily: "font-Inter",
                    fontSize: "text-sm",
                    fontWeight: "font-bold",
                    fontColor: "text-white",
                  }}
                  borderStyle={{ borderRadius: "rounded-lg" }}
                  className={`p-4 ${!verifyButtonDisable ? "hover:bg-blue-600" : ""} w-[10rem]`}
                  onClickHandler={handleSumbmitRemitter}
                  disabled={verifyButtonDisable || remitter?.updateRemitter?.loading}
                  isLoading={remitter?.updateRemitter?.loading}
                >
                  <div className='flex items-center gap-1'>
                    {"Verify"}
                  </div>
                </Button>
                {remitter?.updateRemitter?.error && <span className="ml-4 text-error">{remitter?.updateRemitter?.error}</span>}
              </div>
            </div>
            
          </div>

        </div>
        )}

      </div>
    </>
  )
};

export default RemittanceRemitterView;
